import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signupProblems, type SignupDetails } from '@fsm/shared';
import { useAuth } from '../lib/auth';
import { Button, Field, Input } from '../components/ui';
import { AuthShell } from '../components/AuthShell';

/**
 * Starting a company.
 *
 * Four fields and nothing else. Everything a contractor might reasonably be
 * asked — address, tax rate, logo, timezone — is a thing they can fill in
 * later from Settings, and a sign-up form that asks for all of it up front is
 * a form people abandon. The only genuinely required facts are who they are
 * and how to let them back in.
 *
 * Validated against the same rules the server uses, out of the domain core.
 * The point is not to save a request; it is that a form which accepts a
 * password the server will reject is a form that lies.
 */
export function SignupPage() {
  const { signUp } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState<SignupDetails>({
    companyName: '',
    name: '',
    email: '',
    password: '',
  });
  const [problems, setProblems] = useState<Partial<Record<keyof SignupDetails, string>>>({});
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const set = (key: keyof SignupDetails) => (value: string) => {
    setForm((current) => ({ ...current, [key]: value }));
    /* Clear this field's complaint as they fix it, not on the next submit. */
    setProblems((current) => ({ ...current, [key]: undefined }));
  };

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setFailure(null);

    const found = signupProblems(form);
    setProblems(found);
    if (Object.keys(found).length > 0) return;

    setBusy(true);
    try {
      await signUp(form);
      navigate('/dispatch', { replace: true });
    } catch (error) {
      setFailure((error as { message?: string }).message ?? 'Could not create the account');
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell
      title="Start your company on Crewlark"
      subtitle="Takes a minute. You can fill in the rest later."
    >
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <Field label="Business name" error={problems.companyName}>
          <Input
            value={form.companyName}
            autoComplete="organization"
            placeholder="Quarry Bend Plumbing"
            onChange={(e) => set('companyName')(e.target.value)}
          />
        </Field>
        <Field label="Your name" error={problems.name}>
          <Input
            value={form.name}
            autoComplete="name"
            onChange={(e) => set('name')(e.target.value)}
          />
        </Field>
        <Field label="Email" error={problems.email}>
          <Input
            type="email"
            value={form.email}
            autoComplete="username"
            onChange={(e) => set('email')(e.target.value)}
          />
        </Field>
        <Field label="Password" error={problems.password}>
          <Input
            type="password"
            value={form.password}
            autoComplete="new-password"
            onChange={(e) => set('password')(e.target.value)}
          />
          <p className="mt-1.5 text-[12.5px] leading-relaxed text-ink-4">
            At least 12 characters. A short phrase of a few words is easier to remember and
            harder to guess than a mangled word.
          </p>
        </Field>

        {failure && (
          <p className="rounded-lg bg-danger-soft px-3 py-2 text-[13px] text-danger">{failure}</p>
        )}

        <Button type="submit" variant="primary" size="lg" className="w-full" loading={busy}>
          Create account
        </Button>
      </form>

      <p className="mt-5 text-center text-[13px] text-ink-3">
        Already have one?{' '}
        <Link to="/login" className="font-semibold text-accent">
          Sign in
        </Link>
      </p>
    </AuthShell>
  );
}
